import {
  type UploadFile,
  Button,
  Col,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { FormImagesUploadWrap } from '../formImagesField/styles';
import {
  isUploadFileListUploading,
  MultiImageUpload,
} from '../multiImageUpload';
import ReadOnlyFileUrlGallery from '../readOnlyFileUrlGallery';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUserId, selectUsername } from '../../slices/profile';
import {
  ActiveStatus,
  Issue,
  SuggestionStatus,
  UploadBusinessType,
  YesOrNo,
} from '../../types';
import {
  entityToUploadFileList,
  uploadFileListToEntityFiles,
} from '../../utils/entityUploadFiles';
import { createIssue, updateIssue } from '../../utils/httpRequest';

const IssueForm = ({
  form,
  templateID,
  onSubmit,
  onCancel,
  showFullScreenSpin,
  issue,
  isSuggestion = false,
  isSuggestionReview = false,
  onReview,
  onChange,
  readOnly,
}: {
  form?: FormInstance;
  templateID?: string;
  showFullScreenSpin?: boolean;
  onSubmit?: (issue: Issue) => Promise<void> | void;
  onCancel?: () => void;
  issue?: Issue;
  isSuggestion?: boolean;
  isSuggestionReview?: boolean;
  onReview?: (issue: Issue) => void;
  onChange?: (issue: Issue) => void;
  readOnly?: boolean;
}) => {
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  useEffect(() => {
    console.log(entityToUploadFileList(issue));
    setUploadFileList(entityToUploadFileList(issue));
  }, [issue]);

  const handleCancel = () => {
    setUploadFileList([]);
    if (onCancel) {
      onCancel();
    }
  };

  const handleConfirm = async () => {
    if (isUploading) {
      return;
    }
    try {
      setLoading(true);
      await form?.validateFields();
      const filePayload = uploadFileListToEntityFiles(uploadFileList, issue);

      const newIssue: Issue = {
        ...issue,
        ...form?.getFieldsValue(),
        ...filePayload,
        issueID: issue?.issueID || 0,
        templateID,
        activeStatus: ActiveStatus.ACTIVE,
        isSuggestion:
          issue?.isSuggestion || (isSuggestion ? YesOrNo.YES : YesOrNo.NO),
        suggestionStatus: issue?.suggestionStatus || SuggestionStatus.SUBMITTED,
        ...(isSuggestion &&
          !isSuggestionReview && {
            suggestionSubmitterID: userId,
            suggestionSubmitterName: username,
          }),
        ...(isSuggestionReview && {
          suggestionReviewerID: userId,
          suggestionReviewerName: username,
        }),
      };

      if (onReview) {
        onReview(newIssue);
        return;
      }

      const res = await (issue ? updateIssue(newIssue) : createIssue(newIssue));

      if (res.data) {
        if (onSubmit) {
          onSubmit(res.data);
        }
        form?.resetFields();
        setUploadFileList([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleUploadChange = (list: UploadFile[]) => {
    setUploadFileList(list);
    setIsUploading(isUploadFileListUploading(list));
    const nextFiles = uploadFileListToEntityFiles(list, issue);
    onChange?.({ ...issue, ...nextFiles } as Issue);
  };

  return (
    <Spin spinning={!showFullScreenSpin && loading}>
      <Row gutter={[40, 16]}>
        <Col span={8}>
          {readOnly ? (
            <ReadOnlyFileUrlGallery images={issue?.images} />
          ) : (
            <FormImagesUploadWrap>
              <MultiImageUpload
                businessType={UploadBusinessType.ISSUE}
                value={uploadFileList}
                onChange={handleUploadChange}
                onUploadingChange={setIsUploading}
              />
            </FormImagesUploadWrap>
          )}
        </Col>
        <Col span={16}>
          <Flex vertical>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('issue')}
                </span>
              }
              name="name"
              rules={[
                {
                  required: !readOnly,
                  message: 'Please input issue name!',
                },
              ]}
            >
              {readOnly ? (
                issue?.name
              ) : (
                <Input type="text" placeholder={t('enter')} />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('labels')}
                </span>
              }
              name="labels"
            >
              {readOnly ? (
                issue?.labels || '-'
              ) : (
                <Select
                  options={[{ value: 'label1', label: 'Label 1' }]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('tools')}
                </span>
              }
              name="tools"
            >
              {readOnly ? (
                issue?.tools || '-'
              ) : (
                <Select
                  options={[{ value: 'tool1', label: 'Tools 1' }]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('qualification')}
                </span>
              }
              name="qualification"
            >
              {readOnly ? (
                issue?.qualification || '-'
              ) : (
                <Select
                  options={[
                    { value: 'qualification1', label: 'Qualification 1' },
                  ]}
                  placeholder={t('select')}
                />
              )}
            </Form.Item>
            <Form.Item
              label={
                <span style={{ fontWeight: readOnly ? 'bold' : 'normal' }}>
                  {t('description')}
                </span>
              }
              name="description"
              rules={[
                {
                  required: !readOnly,
                  message: 'Please input description!',
                },
              ]}
            >
              {readOnly ? (
                issue?.description || '-'
              ) : (
                <Input.TextArea placeholder={t('input')} rows={5} />
              )}
            </Form.Item>
            {!readOnly && !isSuggestionReview && (
              <Flex vertical gap={8} align="flex-end">
                {isUploading && (
                  <Typography.Text>
                    Image(s) upload in progress, please submit when it
                    completed!
                  </Typography.Text>
                )}
                <Flex justify="flex-end" gap={16}>
                  <Button type="primary" ghost onClick={handleCancel}>
                    {t('cancel')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleConfirm}
                    disabled={isUploading}
                  >
                    {t('confirm')}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>
          {showFullScreenSpin && <Spin spinning={loading} fullscreen />}
        </Col>
      </Row>
    </Spin>
  );
};

export default IssueForm;
